import { VoiceInputError, type VoiceInputProvider } from './VoiceInputProvider'
import type { Language } from '../types'
import { voiceLocaleForLanguage } from '../i18n/i18n'

interface SpeechRecognitionEventLike {
  results: ArrayLike<{ 0: { transcript: string } }>
}

interface SpeechRecognitionLike {
  lang: string
  interimResults: boolean
  maxAlternatives: number
  start(): void
  abort(): void
  onresult: ((event: SpeechRecognitionEventLike) => void) | null
  onerror: (() => void) | null
  onend: (() => void) | null
}

type SpeechRecognitionConstructor = new () => SpeechRecognitionLike

function getConstructor(): SpeechRecognitionConstructor | undefined {
  if (typeof window === 'undefined') return undefined
  const speechWindow = window as typeof window & {
    SpeechRecognition?: SpeechRecognitionConstructor
    webkitSpeechRecognition?: SpeechRecognitionConstructor
  }
  return speechWindow.SpeechRecognition ?? speechWindow.webkitSpeechRecognition
}

export class BrowserVoiceInputProvider implements VoiceInputProvider {
  get available(): boolean {
    return Boolean(getConstructor())
  }

  listen(language: Language): Promise<string> {
    const Constructor = getConstructor()
    if (!Constructor) return Promise.reject(new VoiceInputError('UNSUPPORTED'))

    return new Promise((resolve, reject) => {
      const recognition = new Constructor()
      let settled = false
      recognition.lang = voiceLocaleForLanguage(language)
      recognition.interimResults = false
      recognition.maxAlternatives = 1
      recognition.onresult = (event) => {
        settled = true
        resolve(event.results[0]?.[0]?.transcript?.trim() ?? '')
      }
      recognition.onerror = () => {
        settled = true
        reject(new VoiceInputError('RECOGNITION_FAILED'))
      }
      recognition.onend = () => {
        if (!settled) reject(new VoiceInputError('ENDED'))
      }
      recognition.start()
    })
  }
}
